/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  isSameDate as defaultIsSameDate,
  getSafeTime as defaultGetSafeTime,
  getStatusConfig as defaultGetStatusConfig
} from '../lib/helpers';

// Default fallback helpers
const defaultParseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: '', zoom: '', other: '', document: '' };
  if (typeof imageUrlField !== 'string') return { full: '', zoom: '', other: '', document: '' };

  if (imageUrlField.startsWith('{') && imageUrlField.endsWith('}')) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || '',
        zoom: parsed.zoom || '',
        other: parsed.other || '',
        document: parsed.document || ''
      };
    } catch (e) {
      // fallback
    }
  }

  if (imageUrlField.includes('|')) {
    const parts = imageUrlField.split('|');
    return {
      full: parts[0] || '',
      zoom: parts[1] || '',
      other: parts[2] || '',
      document: parts[3] || ''
    };
  }

  return { full: imageUrlField, zoom: '', other: '', document: '' };
};

const defaultGetOptimizedImageUrl = (url) => {
  if (!url || typeof url !== 'string' || !url.includes('cloudinary.com')) return url;
  if (url.includes('/upload/c_')) return url;
  return url.replace('/upload/', '/upload/c_limit,w_300,q_auto,f_auto/');
};

const ToolAssemblyPage = ({
  assemblyTab = 'sets',
  setAssemblyTab,
  selectedForAssembly = [],
  setSelectedForAssembly,
  handleConfirmAssembly,
  assemblyHistoryDate = '',
  setAssemblyHistoryDate,
  itemMaster = [],
  supplies = [],
  logs = [],
  isSameDate = defaultIsSameDate,
  getStatusConfig = defaultGetStatusConfig,
  showPreview,
  setShowPreview,
  handleTogglePreview,
  parseImages = defaultParseImages,
  packagingItem,
  setPackagingItem,
  handlePrintBarcode,
  isPrinting,
  scanBlankItem,
  setScanBlankItem,
  targetBoxForPack,
  setTargetBoxForPack,
  getSafeTime = defaultGetSafeTime,
  selectedSetTemplate,
  setSelectedSetTemplate,
  selectedSingleItem,
  setSelectedSingleItem,
  setExpandedImage,
  getOptimizedImageUrl = defaultGetOptimizedImageUrl
}) => {
  const [localAssemblyHistoryDate, setLocalAssemblyHistoryDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [localExpandedImage, setLocalExpandedImage] = useState(null);

  const checkSameDate = isSameDate || defaultIsSameDate;
  const effectiveHistoryDate = assemblyHistoryDate || localAssemblyHistoryDate;
  const handleDateChange = setAssemblyHistoryDate || setLocalAssemblyHistoryDate;
  const optimizeImg = getOptimizedImageUrl || defaultGetOptimizedImageUrl;
  const imgParser = parseImages || defaultParseImages;

  const handleExpandImage = (imgUrl) => {
    if (setExpandedImage) {
      setExpandedImage(imgUrl);
    } else {
      setLocalExpandedImage(imgUrl);
    }
  };

  // Get Set Templates
  const setTemplates = (itemMaster || []).filter(
    (masterLot) => masterLot.is_set && masterLot.category !== 'กล่องหัวกรอ'
  );

  // Get Individual templates (exclude sets and doctor box containers)
  const singleTemplates = (itemMaster || []).filter((masterLot) => {
    if (masterLot.is_set || (masterLot.category !== 'วัสดุคงทน' && masterLot.category !== 'หัวกรอ')) {
      return false;
    }
    return true;
  });

  // Filter logs for assembly history table
  const assemblyLogs = (logs || []).filter((l) => {
    const isToolAssembly =
      (l.warehouse && l.warehouse.includes('จัดเครื่องมือ')) ||
      (l.action && l.action.includes('จัดเสร็จสิ้น')) ||
      (l.action && l.action.includes('จัด'));
    return isToolAssembly && checkSameDate(l.timestamp, effectiveHistoryDate);
  });

  return (
    <div className="sterile-container">
      <div
        className="sterile-card"
        style={{
          background: 'linear-gradient(135deg, #1e1b4b 0%, #311042 50%, #111827 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        {/* Header */}
        <div className="sterile-header" style={{ marginBottom: '25px' }}>
          <div
            style={{
              fontSize: '3.5em',
              marginBottom: '10px',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
            }}
          >
            🔧
          </div>
          <h2>ห้องจัดเครื่องมือ (Tool Assembly Room)</h2>
          <p>จัดรวมเครื่องมือและหัวกรอจากห้องล้างเครื่องมือเข้าเซ็ต</p>
          <div
            className="accent-bar"
            style={{ background: 'linear-gradient(90deg, #a855f7, #ec4899, #6366f1)' }}
          />
        </div>

        {/* Page content segment controls (Sets / Singles) */}
        <div className="handpiece-tabs-container">
          <button
            type="button"
            className={`handpiece-tab-btn tab-sets ${assemblyTab === 'sets' ? 'active' : ''}`}
            onClick={() => setAssemblyTab && setAssemblyTab('sets')}
          >
            📦 จัดเซ็ตเครื่องมือ (Sets Assembly)
          </button>
          <button
            type="button"
            className={`handpiece-tab-btn tab-singles ${assemblyTab === 'singles' ? 'active' : ''}`}
            onClick={() => setAssemblyTab && setAssemblyTab('singles')}
          >
            🦷 จัดของเดี่ยว (Single Items Packing)
          </button>
        </div>

        {/* Floating Confirm Banner */}
        {selectedForAssembly.length > 0 && (
          <div
            style={{
              marginBottom: '25px',
              padding: '16px 20px',
              background: 'rgba(168, 85, 247, 0.1)',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              borderRadius: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px'
            }}
          >
            <span style={{ fontWeight: 'bold', color: '#c084fc', fontSize: '1.1em' }}>
              🔧 เลือกวัสดุที่จัดเสร็จแล้ว {selectedForAssembly.length} รายการ
            </span>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setSelectedForAssembly && setSelectedForAssembly([])}
                className="sterile-confirm-btn"
                style={{
                  width: 'auto',
                  padding: '10px 20px',
                  fontSize: '0.9em',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: 'none'
                }}
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmAssembly}
                className="sterile-confirm-btn"
                style={{
                  width: 'auto',
                  padding: '10px 24px',
                  fontSize: '0.9em',
                  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                  boxShadow: 'none'
                }}
              >
                ✅ ยืนยันจัดเสร็จและส่งตรวจ
              </button>
            </div>
          </div>
        )}

        {/* Content area */}
        <div>
          {/* Mode A: Assemble Sets */}
          {assemblyTab === 'sets' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, color: '#e9d5ff' }}>
                  📋 รายการแม่แบบชุดที่กำหนดไว้ ({setTemplates.length} รายการ)
                </h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {(() => {
                  const cards = [];
                  setTemplates.forEach((masterLot) => {
                    // Calculate availability of components in the washing room
                    const componentStatuses = (masterLot.components || []).map((compId) => {
                      const compItem = (itemMaster || []).find((i) => i.id === compId);
                      const compName = compItem?.item_name || 'ไม่ทราบชื่อ';
                      const compImg = compItem?.image_url;
                      const ws = (supplies || []).find(
                        (s) => s.master_id === compId && s.warehouse_name?.includes('ห้องล้างเครื่องมือ')
                      );
                      const availQty = ws?.item_amount || 0;
                      const cabinet = compItem?.cabinet || '';
                      const shelf = compItem?.shelf || '';
                      const row = compItem?.row || '';
                      const unitSize = compItem?.unit_size || '';
                      const unit = compItem?.unit || 'ชิ้น';
                      const category = compItem?.category || '';
                      return { compId, compName, compImg, availQty, cabinet, shelf, row, unitSize, unit, category };
                    });

                    const minAvailable =
                      componentStatuses.length > 0
                        ? componentStatuses.reduce((min, c) => Math.min(min, c.availQty), 9999)
                        : 0;
                    const isFullyAvailable = minAvailable > 0 && minAvailable !== 9999;
                    const assemblyCount = isFullyAvailable ? minAvailable : 0;

                    for (let idx = 0; idx < assemblyCount; idx++) {
                      const indexInList = idx + 1;
                      const isSelected = selectedSetTemplate?.id === masterLot.id;
                      const itemKey = `set-${masterLot.id}-${indexInList}`;
                      const isItemChecked = (selectedForAssembly || []).some((x) => x.key === itemKey);

                      cards.push(
                        <div
                          key={itemKey}
                          className="handpiece-card set-card"
                          onClick={() => setSelectedSetTemplate && setSelectedSetTemplate(masterLot)}
                          style={{
                            background: isSelected
                              ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(236, 72, 153, 0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(236, 72, 153, 0.05) 100%)',
                            border: isSelected
                              ? '2px solid #c084fc'
                              : '1px solid rgba(236, 72, 153, 0.4)',
                            boxShadow: isSelected ? '0 0 20px rgba(192, 132, 252, 0.6)' : 'none',
                            transition: 'all 0.2s ease',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            cursor: 'pointer'
                          }}
                        >
                          {/* Checkbox at the top */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              background: isItemChecked
                                ? 'rgba(168, 85, 247, 0.2)'
                                : 'rgba(255, 255, 255, 0.03)',
                              border: `1px solid ${isItemChecked ? '#a855f7' : 'rgba(255, 255, 255, 0.1)'}`,
                              alignSelf: 'flex-start'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isItemChecked}
                              onChange={(e) => {
                                if (setSelectedForAssembly) {
                                  if (e.target.checked) {
                                    setSelectedForAssembly((prev) => [
                                      ...prev,
                                      { key: itemKey, type: 'set', masterLot }
                                    ]);
                                  } else {
                                    setSelectedForAssembly((prev) =>
                                      prev.filter((x) => x.key !== itemKey)
                                    );
                                  }
                                }
                              }}
                              style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                            />
                            <span
                              style={{
                                fontSize: '0.85em',
                                fontWeight: 'bold',
                                color: isItemChecked ? '#f472b6' : 'rgba(255,255,255,0.7)'
                              }}
                            >
                              {isItemChecked ? '✅ เลือกแล้ว (จัดเสร็จ)' : '⬜ เลือกรายการนี้ (จัดเสร็จ)'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            {/* Left: Images */}
                            {(() => {
                              const { full, zoom, other, document: doc } = imgParser(masterLot.image_url);
                              const images = [
                                { label: 'รูปเต็ม', url: full },
                                { label: 'รูปซูม', url: zoom },
                                { label: 'รูปอื่นๆ', url: other },
                                { label: 'เอกสาร', url: doc }
                              ].filter((img) => img.url);

                              return (
                                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                  {images.length > 0 ? (
                                    images.map((img, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          width: '100px',
                                          height: '100px',
                                          borderRadius: '10px',
                                          overflow: 'hidden',
                                          border: '1px solid rgba(255, 255, 255, 0.15)',
                                          background: '#ffffff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleExpandImage(optimizeImg(img.url));
                                        }}
                                        title={img.label}
                                      >
                                        <img
                                          src={optimizeImg(img.url)}
                                          alt={img.label}
                                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                      </div>
                                    ))
                                  ) : (
                                    <div
                                      style={{
                                        width: '100px',
                                        height: '100px',
                                        borderRadius: '10px',
                                        border: '1px dashed rgba(255,255,255,0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(255, 255, 255, 0.02)'
                                      }}
                                    >
                                      <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.3)' }}>
                                        ไม่มีรูปภาพ
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Right: Info */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div className="handpiece-card-header-row" style={{ margin: 0, padding: 0 }}>
                                <div className="handpiece-title-text">
                                  <span style={{ color: '#f472b6', fontSize: '1.1em', fontWeight: 'bold' }}>
                                    📦 {masterLot.item_name}
                                  </span>
                                  <small style={{ color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                                    ประเภท: ชุดเครื่องมือ ({masterLot.category})
                                  </small>
                                </div>
                                <span
                                  className="handpiece-badge-qty"
                                  style={{
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    boxShadow: '0 0 10px rgba(16, 185, 129, 0.2)'
                                  }}
                                >
                                  ชุดที่ {indexInList}
                                </span>
                              </div>

                              {(masterLot.cabinet || masterLot.shelf) && (
                                <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.6)' }}>
                                  📍 พิกัดเก็บเดิม: {masterLot.cabinet ? `ตู้: ${masterLot.cabinet}` : ''}{' '}
                                  {masterLot.shelf ? `ชั้น: ${masterLot.shelf}` : ''}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Components list */}
                          <div
                            style={{
                              background: 'rgba(0,0,0,0.15)',
                              borderRadius: '14px',
                              padding: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '12px'
                            }}
                          >
                            <span
                              style={{
                                fontSize: '0.9em',
                                color: 'rgba(255,255,255,0.6)',
                                fontWeight: 'bold'
                              }}
                            >
                              🔍 ชิ้นส่วนที่ต้องใช้ ({(masterLot.components || []).length} รายการ):
                            </span>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                gap: '12px'
                              }}
                            >
                              {componentStatuses.map((c, i) => {
                                const { full, zoom, other, document: doc } = imgParser(c.compImg);
                                const images = [
                                  { label: 'รูปเต็ม', url: full },
                                  { label: 'รูปซูม', url: zoom },
                                  { label: 'รูปอื่นๆ', url: other },
                                  { label: 'เอกสาร', url: doc }
                                ].filter((img) => img.url);

                                const locationText =
                                  c.cabinet || c.shelf || c.row
                                    ? `ตู้: ${c.cabinet || '-'} ชั้น: ${c.shelf || '-'} แถว: ${c.row || '-'}`
                                    : 'ไม่ระบุพิกัด';

                                return (
                                  <div
                                    key={i}
                                    className="handpiece-card single-card"
                                    style={{
                                      background:
                                        c.availQty > 0
                                          ? 'linear-gradient(135deg, rgba(39, 174, 96, 0.08) 0%, rgba(46, 204, 113, 0.03) 100%)'
                                          : 'linear-gradient(135deg, rgba(231, 76, 60, 0.08) 0%, rgba(192, 41, 43, 0.03) 100%)',
                                      border: `1px solid ${c.availQty > 0 ? 'rgba(39, 174, 96, 0.25)' : 'rgba(231, 76, 60, 0.25)'}`,
                                      padding: '12px',
                                      display: 'flex',
                                      gap: '12px',
                                      alignItems: 'center',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s ease',
                                      cursor: 'default'
                                    }}
                                  >
                                    {/* Left: Component Images (100x100px) */}
                                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                      {images.length > 0 ? (
                                        images.map((img, idx) => (
                                          <div
                                            key={idx}
                                            style={{
                                              width: '100px',
                                              height: '100px',
                                              borderRadius: '10px',
                                              overflow: 'hidden',
                                              border: '1px solid rgba(255, 255, 255, 0.15)',
                                              background: '#ffffff',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              cursor: 'pointer',
                                              transition: 'transform 0.2s ease'
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleExpandImage(optimizeImg(img.url));
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                                            onMouseLeave={(e) => (e.currentTarget.style.transform = 'none')}
                                            title={img.label}
                                          >
                                            <img
                                              src={optimizeImg(img.url)}
                                              alt={img.label}
                                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                          </div>
                                        ))
                                      ) : (
                                        <div
                                          style={{
                                            width: '100px',
                                            height: '100px',
                                            borderRadius: '10px',
                                            border: '1px dashed rgba(255,255,255,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'rgba(255, 255, 255, 0.02)',
                                            flexShrink: 0
                                          }}
                                        >
                                          <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.3)' }}>
                                            ไม่มีรูป
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Right: Component Info */}
                                    <div
                                      style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px',
                                        minWidth: 0
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '1.1em' }}>{c.availQty > 0 ? '✔️' : '❌'}</span>
                                        <span
                                          style={{
                                            fontWeight: 'bold',
                                            color: c.availQty > 0 ? '#a7f3d0' : '#fca5a5',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.95em'
                                          }}
                                          title={c.compName}
                                        >
                                          {c.compName}
                                        </span>
                                      </div>

                                      {/* Details: Category and size */}
                                      <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.5)' }}>
                                        หมวดหมู่: {c.category || 'ไม่ระบุ'} {c.unitSize ? `(${c.unitSize})` : ''}
                                      </div>

                                      {/* Details: Location */}
                                      <div
                                        style={{
                                          fontSize: '0.8em',
                                          color: 'rgba(255,255,255,0.6)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px'
                                        }}
                                      >
                                        <span>📍</span>
                                        <span
                                          style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                          title={locationText}
                                        >
                                          {locationText}
                                        </span>
                                      </div>

                                      {/* Quantity available in wash room */}
                                      <div style={{ marginTop: '4px' }}>
                                        <span
                                          style={{
                                            fontSize: '0.8em',
                                            fontWeight: 'bold',
                                            padding: '2px 8px',
                                            borderRadius: '20px',
                                            background: c.availQty > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                            color: c.availQty > 0 ? '#10b981' : '#f87171',
                                            border: `1px solid ${c.availQty > 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                                          }}
                                        >
                                          มีในห้องล้าง: {c.availQty} {c.unit || 'ชิ้น'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  });

                  if (cards.length === 0) {
                    return (
                      <div
                        style={{
                          textAlign: 'center',
                          color: 'rgba(255,255,255,0.4)',
                          padding: '40px',
                          border: '1px dashed rgba(255,255,255,0.1)',
                          borderRadius: '16px'
                        }}
                      >
                        ไม่มีเซ็ตเครื่องมือที่พร้อมจัดจากห้องล้างในขณะนี้
                      </div>
                    );
                  }
                  return cards;
                })()}
              </div>
            </div>
          )}

          {/* Mode B: Assemble Singles */}
          {assemblyTab === 'singles' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, color: '#e9d5ff' }}>
                  🦷 รายการอุปกรณ์เดี่ยวที่รอจัดในห้องล้าง (
                  {
                    singleTemplates.filter((m) => {
                      const ws = (supplies || []).find(
                        (s) => s.master_id === m.id && s.warehouse_name?.includes('ห้องล้างเครื่องมือ')
                      );
                      return (ws?.item_amount || 0) > 0;
                    }).length
                  }{' '}
                  รายการ)
                </h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {(() => {
                  const cards = [];
                  singleTemplates.forEach((masterLot) => {
                    const ws = (supplies || []).find(
                      (s) => s.master_id === masterLot.id && s.warehouse_name?.includes('ห้องล้างเครื่องมือ')
                    );
                    const washingAvail = ws?.item_amount || 0;

                    for (let idx = 0; idx < washingAvail; idx++) {
                      const indexInList = idx + 1;
                      const itemKey = `single-${masterLot.id}-${indexInList}`;
                      const isItemChecked = (selectedForAssembly || []).some((x) => x.key === itemKey);

                      cards.push(
                        <div
                          key={itemKey}
                          className="handpiece-card single-card"
                          style={{
                            background: isItemChecked
                              ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(14, 165, 233, 0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(14, 165, 233, 0.03) 100%)',
                            border: isItemChecked ? '2px solid #06b6d4' : '1px solid rgba(6, 182, 212, 0.2)',
                            boxShadow: isItemChecked ? '0 0 15px rgba(6, 182, 212, 0.4)' : 'none',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px'
                          }}
                        >
                          {/* Checkbox at the top */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              background: isItemChecked
                                ? 'rgba(6, 182, 212, 0.2)'
                                : 'rgba(255, 255, 255, 0.03)',
                              border: `1px solid ${isItemChecked ? '#06b6d4' : 'rgba(255, 255, 255, 0.1)'}`,
                              alignSelf: 'flex-start'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isItemChecked}
                              onChange={(e) => {
                                if (setSelectedForAssembly) {
                                  if (e.target.checked) {
                                    setSelectedForAssembly((prev) => [
                                      ...prev,
                                      { key: itemKey, type: 'single', masterLot }
                                    ]);
                                  } else {
                                    setSelectedForAssembly((prev) =>
                                      prev.filter((x) => x.key !== itemKey)
                                    );
                                  }
                                }
                              }}
                              style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                            />
                            <span
                              style={{
                                fontSize: '0.85em',
                                fontWeight: 'bold',
                                color: isItemChecked ? '#67e8f9' : 'rgba(255,255,255,0.7)'
                              }}
                            >
                              {isItemChecked ? '✅ เลือกแล้ว (จัดเสร็จ)' : '⬜ เลือกรายการนี้ (จัดเสร็จ)'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%' }}>
                            {/* Left: Images (Only show Full image on the card) */}
                            {(() => {
                              const { full } = imgParser(masterLot.image_url);
                              return (
                                <div
                                  style={{ display: 'flex', gap: '8px', flexShrink: 0, cursor: 'pointer' }}
                                  onClick={() => setSelectedSingleItem && setSelectedSingleItem(masterLot)}
                                >
                                  {full ? (
                                    <div
                                      style={{
                                        width: '100px',
                                        height: '100px',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                        background: '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <img
                                        src={optimizeImg(full)}
                                        alt={masterLot.item_name}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                      />
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        width: '100px',
                                        height: '100px',
                                        borderRadius: '10px',
                                        border: '1px dashed rgba(255,255,255,0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(255, 255, 255, 0.02)'
                                      }}
                                    >
                                      <span style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.3)' }}>
                                        ไม่มีรูปเต็ม
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Right: Info */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div className="handpiece-card-header-row" style={{ margin: 0, padding: 0 }}>
                                <div className="handpiece-title-text">
                                  <span
                                    style={{
                                      color: '#67e8f9',
                                      fontSize: '1.1em',
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      textDecorationStyle: 'dotted'
                                    }}
                                    onClick={() => setSelectedSingleItem && setSelectedSingleItem(masterLot)}
                                    title="ดูรายละเอียดเครื่องมือ"
                                  >
                                    🦷 {masterLot.item_name}
                                  </span>
                                  <small style={{ color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                                    หมวดหมู่: {masterLot.category} ({masterLot.unit_size || '-'})
                                  </small>
                                </div>
                                <span
                                  className="handpiece-badge-qty"
                                  style={{
                                    background: 'linear-gradient(135deg, #0288d1 0%, #00acc1 100%)',
                                    boxShadow: '0 0 10px rgba(2,136,209,0.2)'
                                  }}
                                >
                                  ชิ้นที่ {indexInList}
                                </span>
                              </div>

                              <div
                                style={{
                                  fontSize: '0.85em',
                                  color: 'rgba(255,255,255,0.6)',
                                  display: 'flex',
                                  gap: '10px'
                                }}
                              >
                                <span>
                                  📍 พิกัดเก็บเดิม:{' '}
                                  {masterLot.cabinet ? `ตู้: ${masterLot.cabinet}` : ''}{' '}
                                  {masterLot.shelf ? `ชั้น: ${masterLot.shelf}` : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  });

                  if (cards.length === 0) {
                    return (
                      <div
                        style={{
                          textAlign: 'center',
                          color: 'rgba(255,255,255,0.4)',
                          padding: '40px',
                          border: '1px dashed rgba(255,255,255,0.1)',
                          borderRadius: '16px'
                        }}
                      >
                        ไม่มีอุปกรณ์เดี่ยวที่รอจัดในห้องล้างขณะนี้
                      </div>
                    );
                  }
                  return cards;
                })()}
              </div>
            </div>
          )}
        </div>

        {/* 📜 Assembly History Log Table */}
        <div
          style={{
            marginTop: '35px',
            background: 'rgba(0, 0, 0, 0.25)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '24px'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '18px',
              flexWrap: 'wrap',
              gap: '12px'
            }}
          >
            <h3
              style={{
                margin: 0,
                color: '#e9d5ff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '1.2em'
              }}
            >
              📜 ประวัติการจัดเครื่องมือ (Assembly History)
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)' }}>เลือกวันที่:</span>
              <input
                type="date"
                value={effectiveHistoryDate}
                onChange={(e) => handleDateChange && handleDateChange(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(168, 85, 247, 0.4)',
                  background: 'rgba(30, 27, 75, 0.6)',
                  color: '#fff',
                  fontSize: '0.9em',
                  outline: 'none'
                }}
              />
            </div>
          </div>
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
              <thead>
                <tr
                  style={{
                    background: 'rgba(168, 85, 247, 0.15)',
                    color: '#e9d5ff',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <th style={{ padding: '12px 14px', textAlign: 'left' }}>🕒 เวลา</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left' }}>📦 รายการอุปกรณ์ / เซ็ต</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left' }}>⚡ การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {assemblyLogs.map((l, index) => (
                  <tr
                    key={l.id || `${l.timestamp}-${index}`}
                    style={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      color: 'rgba(255,255,255,0.85)'
                    }}
                  >
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      {l.timestamp ? new Date(l.timestamp).toLocaleTimeString('th-TH') : '-'}
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: '500', color: '#c084fc' }}>
                      {l.item_name}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#a7f3d0' }}>{l.action}</td>
                  </tr>
                ))}
                {assemblyLogs.length === 0 && (
                  <tr>
                    <td
                      colSpan="3"
                      style={{ textAlign: 'center', padding: '30px', color: 'rgba(255,255,255,0.4)' }}
                    >
                      ไม่มีประวัติการจัดเครื่องมือในวันที่เลือก
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Fallback Image Preview Modal (if setExpandedImage prop is not provided) */}
      {localExpandedImage && !setExpandedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999,
            cursor: 'pointer'
          }}
          onClick={() => setLocalExpandedImage(null)}
        >
          <img
            src={localExpandedImage}
            alt="Expanded preview"
            style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: '12px' }}
          />
        </div>
      )}
    </div>
  );
};

export default ToolAssemblyPage;
